import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { List, BarChart2, RefreshCw, Trophy } from 'lucide-react';
import { exportToCsv, printReport, ExportPrintBar } from '../../utils/reportExport';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
});

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN');
}

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

const BOOKING_COLS = [
  { header: 'Inquiry #', key: 'inquiry_number' },
  { header: 'Date', key: 'inquiry_date', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '-' },
  { header: 'Status', key: 'status' },
  { header: 'Company', key: 'company_name' },
  { header: 'Customer', key: 'customer_name' },
  { header: 'Branch', key: 'customer_branch_name' },
  { header: 'From', key: 'from_location_name' },
  { header: 'To', key: 'to_location_name' },
  { header: 'Vehicle Type', key: 'vehicle_type_name' },
  { header: 'Qty', key: 'no_of_vehicles' },
  { header: 'Load Type', key: 'load_type' },
  { header: 'Trip', key: 'trip' },
  { header: 'Trip Type', key: 'trip_type' },
  { header: 'Ownership', key: 'vehicle_ownership' },
  { header: 'Source', key: 'is_external', format: v => v ? 'External' : 'Internal' },
  { header: 'Employee', key: 'employee_name' },
];

// ─── Tab 1: Booking Summary List ───────────────────────────────
function BookingSummaryList({ companyId, isSuperAdmin, companies }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.append('status', status);
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    if (companyId) p.append('company_id', companyId);
    fetch(`${API}/api/reports/bookings/summary?${p}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setBookings(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [status, dateFrom, dateTo, companyId]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const total = bookings.length;
  const pending = bookings.filter(b => b.status === 'PENDING').length;
  const confirmed = bookings.filter(b => b.status === 'CONFIRMED').length;
  const rejected = bookings.filter(b => b.status === 'REJECTED').length;

  const handleExport = () => exportToCsv('Booking_Summary', BOOKING_COLS, bookings);
  const handlePrint = () => printReport('Booking Requests Summary', BOOKING_COLS, bookings);

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total', value: total, color: 'text-blue-600' },
          { label: 'Pending', value: pending, color: 'text-yellow-600' },
          { label: 'Confirmed', value: confirmed, color: 'text-green-600' },
          { label: 'Rejected', value: rejected, color: 'text-red-600' },
        ].map(s => (
          <Card key={s.label} className="p-4">
            <p className={`text-2xl font-bold font-manrope ${s.color}`}>{s.value}</p>
            <p className="text-xs text-text-secondary font-ibm mt-1">{s.label}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Company</label>
              <Select value={companyId || '__all__'} onValueChange={v => {/* propagated via parent */}}>
                <SelectTrigger className="font-ibm h-9 w-52"><SelectValue placeholder="All Companies" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Companies</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Status</label>
            <Select value={status || '__all__'} onValueChange={v => setStatus(v === '__all__' ? '' : v)}>
              <SelectTrigger className="font-ibm h-9 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="REJECTED">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Date From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="font-ibm h-9 w-40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Date To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="font-ibm h-9 w-40" />
          </div>
          <Button size="sm" onClick={fetch_} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
            <RefreshCw className="w-3 h-3 mr-1" />Apply
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setStatus(''); setDateFrom(''); setDateTo(''); }} className="h-9">
            Clear
          </Button>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || bookings.length === 0} />
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Inquiry #', 'Date', 'Status', ...(isSuperAdmin ? ['Company'] : []), 'Customer', 'Branch', 'Route', 'Vehicle', 'Load Type', 'Trip', 'Source', 'Employee'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-text-secondary">Loading...</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-text-secondary">No booking requests found</td></tr>
              ) : bookings.map(b => (
                <tr key={b.id} className="hover:bg-gray-50/60">
                  <td className="px-4 py-3 font-semibold text-text-dark font-ibm">{b.inquiry_number || '-'}</td>
                  <td className="px-4 py-3 text-text-dark whitespace-nowrap">{formatDate(b.inquiry_date)}</td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>{b.status}</Badge>
                  </td>
                  {isSuperAdmin && <td className="px-4 py-3 text-text-secondary text-xs">{b.company_name || '-'}</td>}
                  <td className="px-4 py-3 text-text-dark max-w-[120px] truncate">{b.customer_name || '-'}</td>
                  <td className="px-4 py-3 text-text-secondary text-xs">{b.customer_branch_name || '-'}</td>
                  <td className="px-4 py-3 text-text-dark text-xs whitespace-nowrap">
                    {b.from_location_name || '-'} → {b.to_location_name || '-'}
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-xs">{b.vehicle_type_name || '-'}{b.no_of_vehicles > 1 ? ` ×${b.no_of_vehicles}` : ''}</td>
                  <td className="px-4 py-3 text-xs">
                    {b.load_type ? <Badge variant="outline" className="text-xs">{b.load_type}</Badge> : '-'}
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-xs">{b.trip || '-'} / {b.trip_type || '-'}</td>
                  <td className="px-4 py-3">
                    {b.is_external
                      ? <Badge className="bg-purple-100 text-purple-700 text-xs">External</Badge>
                      : <Badge className="bg-gray-100 text-gray-600 text-xs">Internal</Badge>}
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-xs">{b.employee_name || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab 2: Booking Analytics ──────────────────────────────────
function BookingAnalytics({ companyId, isSuperAdmin, companies }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetch_ = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    if (companyId) p.append('company_id', companyId);
    fetch(`${API}/api/reports/bookings/stats?${p}`, { headers: authHeaders() })
      .then(r => r.json()).then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo, companyId]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const handleExport = () => {
    if (!stats) return;
    exportToCsv('Booking_Analytics', [{ header: 'Category', key: 'label' }, { header: 'Sub', key: 'sub' }, { header: 'Count', key: 'count' }], [
      ...stats.by_status.map(x => ({ label: 'By Status', sub: x.label, count: x.count })),
      ...(stats.by_load_type || []).map(x => ({ label: 'By Load Type', sub: x.label, count: x.count })),
      ...(stats.by_trip_type || []).map(x => ({ label: 'By Trip', sub: x.label, count: x.count })),
      ...(stats.by_ownership || []).map(x => ({ label: 'By Ownership', sub: x.label, count: x.count })),
    ]);
  };
  const handlePrint = () => {
    if (!stats) return;
    printReport('Booking Analytics', [{ header: 'Category', key: 'label' }, { header: 'Sub-Label', key: 'sub' }, { header: 'Count', key: 'count' }], [
      ...stats.by_status.map(x => ({ label: 'By Status', sub: x.label, count: x.count })),
      ...(stats.by_load_type || []).map(x => ({ label: 'By Load Type', sub: x.label, count: x.count })),
      ...(stats.by_trip_type || []).map(x => ({ label: 'By Trip Category', sub: x.label, count: x.count })),
      ...(stats.by_ownership || []).map(x => ({ label: 'By Ownership', sub: x.label, count: x.count })),
      ...(stats.top_customers || []).map(x => ({ label: 'Top Customers', sub: x.name, count: x.count })),
    ]);
  };

  const StatBlock = ({ title, items = [], total: blockTotal }) => (
    <Card className="p-5">
      <h3 className="text-sm font-bold text-text-dark mb-4 font-manrope uppercase tracking-wider">{title}</h3>
      <div className="space-y-3">
        {items.map(item => {
          const pct = blockTotal > 0 ? Math.round((item.count / blockTotal) * 100) : 0;
          return (
            <div key={item.label || item.name}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-text-dark font-ibm">{item.label || item.name}</span>
                <span className="text-sm font-bold font-ibm text-text-dark">{item.count} <span className="font-normal text-text-secondary">({pct}%)</span></span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-accent-blue rounded-full" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-sm text-text-secondary">No data</p>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-5">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          {isSuperAdmin && (
            <div>
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Company</label>
              <Select value={companyId || '__all__'} onValueChange={() => {}}>
                <SelectTrigger className="font-ibm h-9 w-52"><SelectValue placeholder="All Companies" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Companies</SelectItem>
                  {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Date From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="font-ibm h-9 w-40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-1">Date To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="font-ibm h-9 w-40" />
          </div>
          <Button size="sm" onClick={fetch_} className="bg-accent-blue hover:bg-accent-blue/90 h-9">
            <RefreshCw className="w-3 h-3 mr-1" />Apply
          </Button>
          <ExportPrintBar onExport={handleExport} onPrint={handlePrint} disabled={loading || !stats} />
        </div>
      </Card>

      {loading ? (
        <Card className="p-10 text-center text-text-secondary">Loading analytics...</Card>
      ) : stats ? (
        <>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Pending', value: stats.pending, color: 'text-yellow-600' },
              { label: 'Confirmed', value: stats.confirmed, color: 'text-green-600' },
              { label: 'Rejected', value: stats.rejected, color: 'text-red-600' },
            ].map(s => (
              <Card key={s.label} className="p-4 text-center">
                <p className={`text-3xl font-bold font-manrope ${s.color}`}>{s.value}</p>
                <p className="text-xs text-text-secondary font-ibm mt-1">{s.label}</p>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <StatBlock title="By Status" items={stats.by_status} total={stats.total} />
            <StatBlock title="By Load Type" items={stats.by_load_type} total={stats.total} />
            <StatBlock title="By Trip Category" items={stats.by_trip_type} total={stats.total} />
            <StatBlock title="By Vehicle Ownership" items={stats.by_ownership} total={stats.total} />
          </div>

          {stats.top_customers && stats.top_customers.length > 0 && (
            <Card className="p-5">
              <h3 className="text-sm font-bold text-text-dark mb-4 font-manrope uppercase tracking-wider flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-500" />Top Customers by Bookings
              </h3>
              <div className="space-y-2">
                {stats.top_customers.map((c, i) => (
                  <div key={c.name} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm text-text-dark font-ibm">{c.name}</span>
                    <span className="text-sm font-bold text-accent-blue font-ibm">{c.count} booking{c.count !== 1 ? 's' : ''}</span>
                    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-accent-blue rounded-full" style={{ width: `${Math.round((c.count / stats.total) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      ) : null}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────
const TABS = [
  { id: 'list', label: 'Booking Summary', icon: List },
  { id: 'analytics', label: 'Analytics', icon: BarChart2 },
];

export default function BookingReports() {
  const [activeTab, setActiveTab] = useState('list');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'super_admin') {
        setIsSuperAdmin(true);
        fetch(`${API}/api/companies`, { headers: authHeaders() })
          .then(r => r.ok ? r.json() : [])
          .then(d => setCompanies(Array.isArray(d) ? d : (d.items || [])));
      }
    } catch { /* ignore */ }
  }, []);

  const handleCompanyChange = (v) => setCompanyId(v === '__all__' ? '' : v);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-dark font-manrope">Booking Reports</h1>
          <p className="text-sm text-text-secondary mt-1">Booking request summary and analytics by status, load type and customer</p>
        </div>

        {/* Super admin company filter — always visible at page level */}
        {isSuperAdmin && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Company:</span>
            <Select value={companyId || '__all__'} onValueChange={handleCompanyChange}>
              <SelectTrigger className="font-ibm h-9 w-56" data-testid="booking-company-filter">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Companies</SelectItem>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Tab Nav */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              data-testid={`tab-booking-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium font-ibm border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-accent-blue text-accent-blue'
                  : 'border-transparent text-text-secondary hover:text-text-dark'
              }`}
            >
              <Icon className="w-4 h-4" />{tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'list' && (
        <BookingSummaryList
          companyId={companyId}
          isSuperAdmin={isSuperAdmin}
          companies={companies}
        />
      )}
      {activeTab === 'analytics' && (
        <BookingAnalytics
          companyId={companyId}
          isSuperAdmin={isSuperAdmin}
          companies={companies}
        />
      )}
    </div>
  );
}
